<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="units"
      :items-per-page="100"
      :options.sync="options"
      :server-items-length="total"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'mdi-arrow-collapse-left',
        lastIcon: 'mdi-arrow-collapse-right',
        prevIcon: 'mdi-arrow-left',
        nextIcon: 'mdi-arrow-right',
        itemsPerPageOptions: [10, 25, 50, 100],
      }"
      :loading="loading"
      multi-sort
      class="elevation-1"
    >
      <template v-slot:top>
        <v-toolbar
          flat
          color="white"
        >
          <v-toolbar-title>{{ $t("lbl.units") }}</v-toolbar-title>
          <v-divider
            class="mx-4"
            inset
            vertical
          />
          <v-spacer />
          <v-btn
            class="mb-2 mr-2"
            width="112"
            color="warning"
            @click="refresh"
          >
            <v-icon>mdi-refresh-circle</v-icon>
          </v-btn>
          <v-dialog
            v-model="dialog"
            max-width="600px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-if="auth('createUnits')"
                color="primary"
                class="mb-2"
                v-bind="attrs"
                v-on="on"
                :disabled="disable"
              >{{ $t("lbl.newUnit") }}</v-btn>
            </template>
            <v-form
              ref="form"
              v-model="valid"
            >
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                      >
                        <v-text-field
                          :readonly="editedIndex !== -1"
                          v-model="editedItem.uid"
                          :rules="rules.uidRules"
                          :label="$t('lbl.uid')"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                        md="6"
                      >
                        <v-text-field
                          :readonly="editedIndex !== -1"
                          v-model="editedItem.sn"
                          :rules="rules.snRules"
                          :label="$t('lbl.sn')"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                        md="6"
                      >
                        <v-select
                          v-model="editedItem.edition"
                          :items="editions"
                          :rules="rules.editionRules"
                          :label="$t('lbl.edition')"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                        md="6"
                      >
                        <v-text-field
                          :readonly="editedIndex !== -1"
                          v-model="editedItem.version"
                          :rules="rules.versionRules"
                          :label="$t('lbl.version')"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                        md="6"
                      >
                        <v-select
                          v-model="editedItem.reseller"
                          :items="resellers"
                          item-text="name"
                          item-value="id"
                          :rules="rules.resellerRules"
                          :label="$t('lbl.reseller')"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                        md="6"
                      >
                        <v-select
                          v-model="editedItem.retailer"
                          :items="retailers"
                          item-text="name"
                          item-value="id"
                          :label="$t('lbl.retailer')"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                        md="6"
                      >
                        <v-select
                          v-model="editedItem.customer"
                          :items="customers"
                          item-text="name"
                          item-value="id"
                          :label="$t('lbl.customer')"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                      >
                        <v-select
                          v-model="editedItem.wireless"
                          :items="wireless"
                          :label="$t('lbl.wireless')"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                      >
                        <v-text-field
                          v-model="editedItem.notes"
                          :label="$t('lbl.notes')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="reset"
                  >{{ $t("lbl.clear") }}</v-btn>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="dialog = false"
                  >{{ $t("lbl.cancel") }}</v-btn>
                  <v-btn
                    color="blue darken-1"
                    text
                    :disabled="!valid"
                    @click="save"
                  >{{ $t("lbl.save") }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </v-dialog>
        </v-toolbar>
        <v-container>
          <v-row>
            <v-col
              cols="12"
              sm="12"
              md="2"
            >
              <v-text-field
                outlined
                dense
                hide-details
                v-model="filter.search"
                :label="$t('lbl.unitSearch')"
                @keydown.enter="initialize(true)"
              />
            </v-col>
            <v-col
              v-if="auth(['get', 'fetchResellerUsers'])"
              cols="12"
              sm="6"
              md="2"
            >
              <v-autocomplete
                outlined
                dense
                hide-details
                clearable
                v-model="filter.user"
                :items="users"
                item-text="email"
                item-value="id"
                :label="$t('lbl.user')"
                @change="initialize(true)"
              />
            </v-col>
            <v-col
              v-if="auth('get')"
              cols="12"
              sm="6"
              md="2"
            >
              <v-select
                outlined
                dense
                hide-details
                clearable
                v-model="filter.reseller"
                :items="resellers"
                item-text="name"
                item-value="id"
                :label="$t('lbl.reseller')"
                @change="initialize(true)"
              />
            </v-col>
            <v-col
              v-if="auth(['get', 'fetchResellerUsers'])"
              cols="12"
              sm="6"
              md="2"
            >
              <v-autocomplete
                outlined
                dense
                hide-details
                clearable
                v-model="filter.retailer"
                :items="retailers"
                item-text="name"
                item-value="id"
                :label="$t('lbl.retailer')"
                @change="initialize(true)"
              />
            </v-col>
            <v-col
              v-if="auth(['get', 'fetchResellerUsers'])"
              cols="12"
              sm="6"
              md="2"
            >
              <v-autocomplete
                outlined
                dense
                hide-details
                clearable
                v-model="filter.customer"
                :items="customers"
                item-text="name"
                item-value="id"
                :label="$t('lbl.customer')"
                @change="initialize(true)"
              />
            </v-col>
            <v-col
              v-if="auth('get')"
              cols="12"
              sm="6"
              md="2"
            >
              <v-select
                outlined
                dense
                hide-details
                clearable
                v-model="filter.wireless"
                :items="wireless"
                :label="$t('lbl.wireless')"
                @change="initialize(true)"
              />
            </v-col>

            <v-col
              v-if="auth(['get', 'fetchResellerUsers'])"
              cols="12"
              sm="6"
              md="2"
            >
              <v-select
                outlined
                dense
                hide-details
                clearable
                v-model="filter.licence"
                :items="licences"
                :label="$t('lbl.licence')"
                @change="initialize(true)"
              />
            </v-col>
            <v-col
              v-if="auth('get')"
              cols="12"
              sm="6"
              md="2"
            >
              <v-select
                outlined
                dense
                hide-details
                clearable
                v-model="filter.vpn"
                :items="yesno"
                :label="$t('lbl.vpn')"
                @change="initialize(true)"
              />
            </v-col>
            <v-col
              v-if="auth('get')"
              cols="12"
              sm="6"
              md="2"
            >
              <v-select
                outlined
                dense
                hide-details
                clearable
                v-model="filter.unboxed"
                :items="unbox"
                :label="$t('lbl.unbox')"
                @change="initialize(true)"
              />
            </v-col>
            <template v-if="auth(['get', 'fetchResellerUsers'])">
              <v-col
                cols="12"
                sm="6"
                md="2"
              >
                <v-datetime-picker
                  v-model="filter.start"
                  :label="$t('lbl.start')"
                  :clearText="$t('lbl.clear')"
                  :okText="$t('lbl.ok')"
                  :textFieldProps="{
                    outlined: true,
                    dense: true,
                    hideDetails: true,
                    clearable: true,
                  }"
                  :datePickerProps="{ locale: $i18n.locale, 'no-title': true }"
                  :timePickerProps="{ format: '24hr', 'no-title': true }"
                  @input="initialize(true)"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="2"
              >
                <v-datetime-picker
                  v-model="filter.end"
                  :label="$t('lbl.end')"
                  :clearText="$t('lbl.clear')"
                  :okText="$t('lbl.ok')"
                  :textFieldProps="{
                    outlined: true,
                    dense: true,
                    hideDetails: true,
                    clearable: true,
                  }"
                  :datePickerProps="{ locale: $i18n.locale, 'no-title': true }"
                  :timePickerProps="{ format: '24hr', 'no-title': true }"
                  @input="initialize(true)"
                />
              </v-col>
            </template>
          </v-row>
        </v-container>
      </template>
      <template v-slot:[`item.sn`]="{ item }">
        <v-chip
          label
          outlined
          @click="clipboard(item.sn)"
        >
          {{item.sn}}
        </v-chip>
      </template>
      <template v-slot:[`item.edition`]="{ item }">
        <span>{{ getEnumText(editions, item.edition) }}</span>
      </template>
      <template v-slot:[`item.licence`]="{ item }">
        <span>{{ `${getEnumText(licences, item.licence)}&nbsp;(${item.licperiod})` }}</span>
      </template>
      <template v-slot:[`item.licend`]="{ item }">
        <span>
          {{ item.licend && $luxon.fromISO(item.licend).toLocaleString() }}
        </span>
      </template>
      <template v-slot:[`item.reseller`]="{ item }">
        <span> {{ item.reseller.name }} </span>
      </template>
      <template v-slot:[`item.unboxdate`]="{ item }">
        <span>
          {{ item.unboxdate && $luxon.fromISO(item.unboxdate).toLocaleString() }}
        </span>
      </template>
      <template v-slot:[`item.description`]="{ item }">
        <v-chip
          label
          outlined
        >
          <v-edit-dialog
            large
            :save-text="$t('lbl.save')"
            :cancel-text="$t('lbl.cancel')"
            @open="description = item.description"
            @save="saveDescription(item.id)"
          >
            {{ item.description }}
            <v-icon
              v-if="!item.description"
              small
            >
              mdi-note-text-outline
            </v-icon>
            <template v-slot:input>
              <v-text-field
                v-model="description"
                :label="$t('lbl.description')"
              />
            </template>
          </v-edit-dialog>
        </v-chip>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip
          top
          :disabled="!(item.ip || item.sip)"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-if="auth(['supportUnits', 'operateUnits'])"
              v-bind="attrs"
              v-on="on"
              class="mr-2"
              :disabled="disable"
              :color="color({ ip: item.ip })"
              @click="openOperate(item)"
            > mdi-lan-connect</v-icon>

            <v-icon
              v-if="auth('supportUnits')"
              v-bind="attrs"
              v-on="on"
              class="mr-2"
              :disabled="disable"
              :color="color({ sip: item.sip })"
              @click="support(item)"
            > mdi-connection</v-icon>

            <v-icon
              v-if="auth(['supportUnits', 'operateUnits'])"
              v-bind="attrs"
              v-on="on"
              class="mr-2"
              :disabled="disable"
              :color="colorDc(item)"
              @click="openOperateDc(item)"
            > mdi-lan-disconnect</v-icon>
          </template>
          <span>{{ item.ip ? item.ip : item.sip }}</span>
        </v-tooltip>
        <!-- <v-icon
          v-if="auth('supportUnits')"
          class="mr-2"
          :disabled="disable"
          color="warning"
          @click="unitReset(item)"
        > mdi-database-refresh</v-icon> -->
        <v-icon
          v-if="auth('manageUnits')"
          class="mr-2"
          :disabled="disable"
          @click="editItem(item)"
        >mdi-pencil</v-icon>
        <v-icon
          v-if="auth('manageUnits')"
          :disabled="disable"
          @click="deleteItem(item)"
        >mdi-delete</v-icon>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialogOperate"
      max-width="400"
    >
      <v-flex text-center>
        <v-card>
          <v-card-title />
          <v-card-text>
            <v-icon
              x-large
              color="warning"
            >mdi-alert</v-icon>
            <p class="text-body-1 text--primary ma-0">
              <span v-html="$t('lbl.vpnConfigHelp')" /><br />
              <a
                @click="config"
                @keypress="config"
              >{{ $t("lbl.vpnConfig") }}</a>
            </p>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn
              depressed
              min-width="100"
              color="primary"
              @click="operate()"
            >{{ $t("lbl.ok") }}</v-btn>
            <v-btn
              depressed
              min-width="100"
              color="error"
              @click="dialogOperate = false"
            >{{ $t("lbl.cancel") }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-dialog>

    <v-dialog
      v-model="dialogOperateDc"
      max-width="400"
    >
      <v-flex text-center>
        <v-card>
          <v-card-title />
          <v-card-text>
            <v-icon
              x-large
              color="warning"
            >mdi-alert</v-icon>
            <p class="text-body-1 text--primary ma-0">
              <span v-html="$t('lbl.vpnDisconnect')" />
            </p>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn
              depressed
              min-width="100"
              color="primary"
              @click="operateDc()"
            >{{ $t("lbl.ok") }}</v-btn>
            <v-btn
              depressed
              min-width="100"
              color="error"
              @click="dialogOperateDc = false"
            >{{ $t("lbl.cancel") }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { saveAs } from "file-saver";
import { required, semver, guid } from "../common/validation";
import {
  unbox, editions, wireless, licences, yesno,
} from "../common/enums";

export default {
  data: () => ({
    dialog: false,
    dialogOperate: false,
    dialogOperateDc: false,
    disable: false,
    valid: true,
    loading: true,
    options: {},
    total: 0,
    units: [],
    resellers: [],
    users: [],
    retailers: [],
    customers: [],
    editedIndex: -1,
    editedItem: {
      uid: "",
      sn: "",
      edition: "",
      version: "",
      reseller: null,
      retailer: null,
      customer: null,
      wireless: "",
      notes: "",
    },
    defaultItem: {
      uid: "",
      sn: "",
      edition: "",
      version: "",
      reseller: null,
      retailer: null,
      customer: null,
      wireless: "",
      notes: "",
    },
    operatingUnit: null,
    description: "",
    rules: {
      uidRules: [required, guid],
      snRules: [required],
      resellerRules: [required],
      versionRules: [required, semver],
      editionRules: [required],
    },
    filter: {
      search: null,
      reseller: null,
      user: null,
      start: null,
      end: null,
      unboxed: null,
      wireless: null,
      licence: null,
      vpn: null,
    },
  }),
  sockets: {
    processed() {
      this.initialize();
    },
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("lbl.newUnit")
        : this.$t("lbl.editUnit");
    },
    headers() {
      const columns = [
        // { text: this.$t("lbl.uid"), value: "uid" },
        { text: this.$t("lbl.sn"), value: "sn", width: "5%" },
        { text: this.$t("lbl.edition"), value: "edition" },
        { text: this.$t("lbl.version"), value: "version" },
        { text: this.$t("lbl.boxver"), value: "boxver" },
      ];
      if (this.auth(["get", "fetchResellerUsers"])) {
        columns.push({ text: this.$t("lbl.user"), value: "user.email" });
      }
      columns.push({ text: this.$t("lbl.licence"), value: "licence" });
      columns.push({ text: this.$t("lbl.licend"), value: "licend" });
      if (this.auth("get")) {
        columns.push({ text: this.$t("lbl.reseller"), value: "reseller" });
      }
      if (this.auth(["get", "fetchResellerUsers"])) {
        columns.push({ text: this.$t("lbl.retailer"), value: "retailer.name" });
        columns.push({ text: this.$t("lbl.customer"), value: "customer.name" });
      }
      if (this.auth("get")) {
        columns.push(
          { text: this.$t("lbl.wireless"), value: "wireless" },
          { text: this.$t("lbl.notes"), value: "notes" },
        );
      }
      if (this.auth("reviseUnits")) {
        columns.push({
          text: this.$t("lbl.description"),
          value: "description",
        });
      }
      columns.push({ text: this.$t("lbl.date"), value: "unboxdate" });

      if (this.auth(["manageUnits", "supportUnits", "operateUnits"])) {
        columns.push({
          text: this.$t("lbl.actions"),
          value: "actions",
          align: "center",
          sortable: false,
          width: "8%",
        });
      }
      return columns;
    },
    unbox() {
      return unbox();
    },
    editions() {
      return editions();
    },
    wireless() {
      return wireless();
    },
    licences() {
      return licences();
    },
    yesno() {
      return yesno();
    },
  },

  watch: {
    dialog(val) {
      // eslint-disable-next-line no-unused-expressions
      val || this.close();
    },
    options: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions("unit", [
      "findUnits",
      "fetchUnits",
      "fetchResellerUnits",
      "getUnit",
      "saveUnit",
      "deleteUnit",
      "getVpnConfig",
      "fetchVpnConfig",
      "supportUnit",
      "operateUnit",
      "unitSupport",
      "resetUnit",
      "unitDescription",
    ]),
    ...mapActions("reseller", ["findResellers"]),
    ...mapActions("user", ["findUsers", "fetchResellerUsers"]),
    ...mapActions("retailer", ["findRetailers", "findResellerRetailers"]),
    ...mapActions("customer", ["findCustomers", "findResellerCustomers"]),

    getRequester(req) {
      let requester;
      switch (req) {
        case "find":
          if (this.auth("getUnits")) {
            requester = this.findUnits;
          } else if (this.auth("fetchUnits")) {
            requester = this.fetchUnits;
          } else if (this.auth("fetchResellerUnits")) {
            requester = this.fetchResellerUnits;
          }
          break;
        default:
          break;
      }
      return requester;
    },

    async initialize(search) {
      this.loading = true;
      if (search) {
        this.options.page = 1;
      }
      const filters = this.getFilters(this.options, this.filter);
      try {
        const requester = this.getRequester("find");
        const { results, totalResults } = await requester(filters);
        this.units = results;
        this.total = totalResults;
      } catch (error) {
        this.$toasted.error(this.getMessageText(error.message));
      } finally {
        this.loading = false;
      }

      if (this.auth("get")) {
        try {
          const { results } = await this.findResellers({
            limit: 0,
            select: "name",
          });
          this.resellers = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }

        try {
          const { results } = await this.findUsers({
            limit: 0,
            select: "email",
          });
          results.unshift({ id: "null", email: "-" });
          this.users = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }

        try {
          const options = {
            limit: 0,
            select: "name",
          };

          const { results } = await this.findRetailers(options);
          results.unshift({ id: "null", name: "-" });
          this.retailers = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }

        try {
          const options = {
            limit: 0,
            select: "name",
          };
          const { results } = await this.findCustomers(options);
          results.unshift({ id: "null", name: "-" });
          this.customers = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }
      }

      if (this.auth("fetchResellerUsers")) {
        try {
          const options = {
            limit: 0,
            select: "name",
          };
          const { results } = await this.fetchResellerUsers(options);
          this.users = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }

        try {
          const options = {
            limit: 0,
            select: "name",
          };
          const { results } = await this.findResellerRetailers(options);
          results.unshift({ id: "null", name: "-" });
          this.retailers = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }

        try {
          const options = {
            limit: 0,
            select: "name",
          };
          const { results } = await this.findResellerCustomers(options);
          results.unshift({ id: "null", name: "-" });
          this.customers = results;
        } catch (error) {
          this.$toasted.error(this.getMessageText(error.message));
        }
      }
    },

    reset() {
      this.$refs.form.reset();
    },

    close() {
      this.reset();
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
      });
    },

    color({ ip, sip }) {
      if (ip) {
        return ip ? "success" : "error";
      }
      if (sip) {
        return sip ? "info" : "error";
      }
      return "error";
    },

    colorDc(item) {
      const { ip, vpn } = item;
      if (!ip) {
        return "error";
      }
      return vpn ? "warning" : "grey";
    },

    editItem(item) {
      this.editedIndex = this.units.indexOf(item);
      const {
        reseller, retailer, customer, ...body
      } = { ...item };
      this.editedItem = {
        ...body,
        reseller: reseller.id,
        retailer: retailer ? retailer.id : null,
        customer: customer ? customer.id : null,
      };
      this.dialog = true;
    },

    deleteItem(item) {
      this.$swal({
        html: this.$t("msg.deleteText", {
          item: `${item.sn ? item.sn : item.uid} - ${item.reseller.name}`,
        }),
        icon: "warning",
        showCancelButton: true,
        focusCancel: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("lbl.delete"),
        cancelButtonText: this.$t("lbl.cancel"),
      }).then((result) => {
        if (result.value) {
          this.disable = true;
          this.deleteUnit(item.id)
            .then(() => {
              this.initialize();
              this.$toasted.success(
                this.$t("msg.delete", { item: this.$t("lbl.unit") }),
              );
            })
            .catch((error) => {
              this.$toasted.error(this.getMessageText(error.message));
            })
            .finally(() => {
              this.disable = false;
            });
        }
      });
    },

    save() {
      this.dialog = false;
      this.disable = true;
      const editedItem = { ...this.editedItem };
      return this.saveUnit(editedItem)
        .then(() => {
          this.initialize();
          this.$toasted.success(
            this.$t("msg.save", { item: this.$t("lbl.unit") }),
          );
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        })
        .finally(() => {
          this.disable = false;
        });
    },

    refresh() {
      Object.keys(this.filter).forEach((i) => {
        this.filter[i] = null;
      });
      this.initialize();
    },

    openOperate(item) {
      if (item.ip) {
        this.operatingUnit = item;
        this.dialogOperate = true;
      }
    },

    openOperateDc(item) {
      if (item.ip) {
        this.operatingUnit = item;
        this.dialogOperateDc = true;
      }
    },

    operate() {
      this.dialogOperate = false;
      const connectUnit = this.auth("supportUnits")
        ? this.supportUnit
        : this.operateUnit;
      const { id } = this.operatingUnit;
      connectUnit({ id })
        .then(({ ip, key }) => {
          window.open(`http://${ip}/operate/${key}`);
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        });
    },

    operateDc() {
      this.dialogOperateDc = false;
      const disconnectUnit = this.auth("supportUnits")
        ? this.supportUnit
        : this.operateUnit;
      const { id } = this.operatingUnit;
      disconnectUnit({ id, disconnect: true })
        .then(() => {
          this.$toasted.success(this.$t("lbl.disconnected"));
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        });
    },

    config() {
      const vpnConfig = this.auth("supportUnits")
        ? this.getVpnConfig
        : this.fetchVpnConfig;
      vpnConfig()
        .then((result) => {
          saveAs(
            new Blob([result.content], {
              type: "text/plain;charset=utf-8",
            }),
            result.name,
          );
          this.$toasted.success(
            this.$t("msg.download", { item: this.$t("lbl.vpnConfig") }),
          );
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        });
    },

    support(item) {
      this.unitSupport(item.id)
        .then(() => {
          this.$toasted.success(this.$t("lbl.ok"));
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        });
    },

    unitReset(item) {
      this.$swal({
        html: this.$t("msg.reset"),
        icon: "warning",
        input: "checkbox",
        inputPlaceholder: "Full Reset",
        showCancelButton: true,
        focusCancel: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("lbl.reset"),
        cancelButtonText: this.$t("lbl.cancel"),
      }).then((result) => {
        if (result.isConfirmed) {
          this.resetUnit({ id: item.id, full: !!result.value })
            .then(() => {
              this.$toasted.success(this.$t("lbl.ok"));
            })
            .catch((error) => {
              this.$toasted.error(this.getMessageText(error.message));
            });
        }
      });
    },

    saveDescription(id) {
      const { description } = this;
      this.unitDescription({ id, description })
        .then(() => {
          this.initialize();
          this.$toasted.success(
            this.$t("msg.save", { item: this.$t("lbl.description") }),
          );
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        });
    },
  },
};
</script>

<style>
.swal2-html-container {
  font-size: 26px !important;
}
.swal2-checkbox,
.swal2-radio {
  font-size: 22px;
  color: red !important;
}
</style>
